import React, { useState } from 'react';
import type { Role, Menu, CreateRoleDto } from '../types';

interface Props {
  roles: Role[];
  menus: Menu[];
  onAddRole: (role: CreateRoleDto) => Promise<void>;
  onEditRole: (roleIdx: number, role: CreateRoleDto) => Promise<void>;
  onDeleteRole: (roleIdx: number) => Promise<void>;
  onRefresh: () => void;
}

interface RoleMenuResponse {
  menuIdx: number;
  name: string;
  isActive: string;
  isAssigned: boolean;
}

const RESERVED_ADMIN_ROLE_NAMES = new Set(['관리자', '최고관리자', 'admin', 'administrator']);
const RESERVED_ADMIN_ROLE_ERROR = '관리자 역할명은 시스템 전용입니다.';
const ADMIN_ROLE_PROTECTED_ERROR = '시스템 관리자 역할은 수정/삭제할 수 없습니다.';

const isAdminRole = (role: Role) => role.isAdmin || role.roleType === 'ADMIN';

export default function RolesTab({ roles, menus, onAddRole, onDeleteRole, onRefresh }: Props) {
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([]);
  const [modalMenus, setModalMenus] = useState<any[]>([]);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [isLoadingMenu, setIsLoadingMenu] = useState(false);

  const fetchRoleMenus = async (roleIdx: number) => {
    setIsLoadingMenu(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/auth/permission/role/${roleIdx}/menu`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.status && Array.isArray(result.data)) {
        setModalMenus(result.data);
        const assignedIds = result.data
          .filter((item: RoleMenuResponse) => item.isAssigned === true)
          .map((item: RoleMenuResponse) => item.menuIdx);
        setSelectedMenuIds(assignedIds);
      }
    } catch (error) {
      console.error('Fetch Role Menus Error:', error);
      setModalMenus(menus);
    } finally {
      setIsLoadingMenu(false);
    }
  };

  const updateRoleWithMenus = async (roleIdx: number, data: { name: string; description: string; menuIds: number[] }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/auth/permission/role/${roleIdx}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '수정 실패');
      }
    } catch (error) {
      console.error('Update Role Error:', error);
      const message = error instanceof Error ? error.message : '저장 중 오류가 발생했습니다.';
      alert(message);
      throw error;
    }
  };

  const handleEditRole = async (role: Role) => {
    if (isAdminRole(role)) {
      alert(ADMIN_ROLE_PROTECTED_ERROR);
      return;
    }
    setSelectedRole(role);
    setFormData({ name: role.name, description: role.description });
    setShowRoleModal(true);
    await fetchRoleMenus(role.roleIdx);
  };

  const handleAddRoleClick = () => {
    setSelectedRole(null);
    setFormData({ name: '', description: '' });
    setModalMenus(menus);
    setSelectedMenuIds([]);
    setShowRoleModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedName = formData.name.trim();
    if (!normalizedName) {
      alert('역할명을 입력해주세요.');
      return;
    }
    if (RESERVED_ADMIN_ROLE_NAMES.has(normalizedName.toLowerCase()) || RESERVED_ADMIN_ROLE_NAMES.has(normalizedName)) {
      alert(RESERVED_ADMIN_ROLE_ERROR);
      return;
    }
    const requestData = {
      name: normalizedName,
      description: formData.description,
      menuIds: selectedMenuIds,
    };
    try {
      if (selectedRole) {
        if (isAdminRole(selectedRole)) {
          alert(ADMIN_ROLE_PROTECTED_ERROR);
          return;
        }
        await updateRoleWithMenus(selectedRole.roleIdx, requestData);
        alert('수정되었습니다. 메뉴 적용을 위해 로그아웃 후 다시 로그인해주세요.');
      } else {
        await onAddRole({ name: normalizedName, description: formData.description, accessibleMenuIds: selectedMenuIds });
      }
      setShowRoleModal(false);
      onRefresh();
    } catch (error) {
      // Error handled inside
    }
  };

  const toggleMenuSelection = (menuId: number) => {
    setSelectedMenuIds(prev =>
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMenuIds.length === modalMenus.length) {
      setSelectedMenuIds([]);
    } else {
      setSelectedMenuIds(modalMenus.map(m => m.menuIdx || m.id));
    }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={handleAddRoleClick} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
          <i className="ri-shield-user-line mr-2"></i> 역할 추가
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <div key={role.roleIdx} className="bg-white rounded-lg shadow p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-gray-800">{role.name}</h3>
                  {isAdminRole(role) && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                      관리자
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 line-clamp-2 min-h-[2.5rem]">{role.description}</p>
              </div>
              <button
                onClick={() => handleEditRole(role)}
                disabled={isAdminRole(role)}
                title={isAdminRole(role) ? ADMIN_ROLE_PROTECTED_ERROR : '역할 수정'}
                className="text-gray-400 hover:text-blue-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="ri-edit-line text-lg"></i>
              </button>
            </div>
            <div className="mb-4 pt-4 border-t border-gray-100">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">접근 권한</div>
              <div className="flex flex-wrap gap-2">
                {role.accessibleMenuIds && role.accessibleMenuIds.length > 0 ? (
                  role.accessibleMenuIds.slice(0, 5).map((id) => {
                    const menu = menus.find(m => (m.id || (m as any).menuIdx) === id);
                    return menu ? (
                      <span key={id} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-md border border-blue-100">
                        {menu.name}
                      </span>
                    ) : null;
                  })
                ) : (
                  <span className="text-xs text-gray-400">권한 없음</span>
                )}
                {role.accessibleMenuIds && role.accessibleMenuIds.length > 5 && (
                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-md">
                    +{role.accessibleMenuIds.length - 5}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-500 mt-2">
              <i className="ri-user-line mr-1.5"></i>
              <span>{role.userCount}명의 사용자</span>
            </div>
            <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => onDeleteRole(role.roleIdx)}
                disabled={isAdminRole(role)}
                title={isAdminRole(role) ? ADMIN_ROLE_PROTECTED_ERROR : '역할 삭제'}
                className="px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="ri-delete-bin-line mr-1"></i>
                삭제
              </button>
            </div>
          </div>
        ))}
      </div>

      {showRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-800">{selectedRole ? '역할 수정' : '역할 추가'}</h2>
              <button onClick={() => setShowRoleModal(false)}><i className="ri-close-line text-2xl"></i></button>
            </div>
            <div className="p-6 overflow-y-auto">
              <form id="roleForm" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">역할명 <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="예: 촬영팀장, 셀렉담당자" className="w-full border rounded-lg px-3 py-2.5" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">설명</label>
                    <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="이 역할의 주요 업무와 책임을 입력해 주세요" className="w-full border rounded-lg px-3 py-2.5 h-24 resize-none" />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">메뉴 권한 설정</label>
                    <button type="button" onClick={handleSelectAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                      {selectedMenuIds.length === modalMenus.length ? '전체 해제' : '전체 선택'}
                    </button>
                  </div>
                  {isLoadingMenu ? (
                    <div className="p-8 text-center text-gray-500">권한 정보를 불러오는 중...</div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-60 overflow-y-auto">
                      <div className="grid grid-cols-2 gap-3">
                        {modalMenus.length === 0 ? (
                          <div className="col-span-2 text-center text-gray-500 py-4">설정 가능한 메뉴가 없습니다.</div>
                        ) : (
                          modalMenus.map(menu => {
                            const menuId = menu.menuIdx || menu.id;
                            const isChecked = selectedMenuIds.includes(menuId);
                            return (
                              <label key={menuId} className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                                isChecked ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-gray-200 hover:border-blue-300'
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleMenuSelection(menuId)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                />
                                <div className="ml-3 flex items-center">
                                  <i className={`${menu.icon || 'ri-checkbox-blank-circle-line'} text-gray-500 mr-2`}></i>
                                  <span className={`text-sm ${isChecked ? 'font-medium text-blue-700' : 'text-gray-700'}`}>
                                    {menu.name}
                                  </span>
                                </div>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-gray-500 text-right">
                    선택됨: <span className="font-semibold text-blue-600">{selectedMenuIds.length}</span> / {modalMenus.length}
                  </p>
                </div>
              </form>
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <button type="button" onClick={() => setShowRoleModal(false)} className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors">취소</button>
              <button type="submit" form="roleForm" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm transition-colors">{selectedRole ? '수정 완료' : '역할 생성'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
